import { useMutation } from '@apollo/client';
import { getFeatureFlags } from 'core/userContext';
import { displayContent } from 'modules/templateEditor/common';
import { addNegativeToastWithErrorId, addPositiveToast } from 'modules/toastContainer';
import { SWITCH_PROJECTS_MODAL } from 'modules/workflows/constants';
import { openModal } from 'modules/workflows/ducks';
import { WorkflowType } from 'modules/workflows/enums';
import { useDispatch, useSelector } from 'react-redux';
import { TrimmedWorkflow } from '../CloneJourneyModal.types';
import { cloneJourneyMutation } from '../cloning.graphql';
import { ERROR_MESSAGE, LOCALES_BETA_CLONING_JOURNEY_ERROR_MESSAGE } from '../constants';
import { CloneFormSchemaType } from '../context/CloneJourneyFormProvider';
import { getCampaignValues } from '../utils';

const { errMsgCopyJourneyFromLocalesBetaToNonBeta, errMsgCopyJourneyFromNonBetaToLocalesBeta } =
  displayContent;

/**
 * Hook to clone journeys cross project
 * used on index page and journey
 * @param workflowName Name to return in success toast
 * @param handleSwitchToProjectJourney Success callback
 * @returns handleCloneJourney mutation function
 */
export const useCloneJourneyCrossProject = ({
  workflowName,
  handleSwitchToProjectJourney,
}: {
  workflowName: string;
  handleSwitchToProjectJourney?: (jId: number, pId: number) => void;
}) => {
  const dispatch = useDispatch();
  const featureFlags = useSelector(getFeatureFlags);
  const isCurrentProjInLocaleBeta = featureFlags.LocaleImprovements;

  const [cloneJourneyToProject] = useMutation(cloneJourneyMutation, {
    onCompleted: ({ cloneJourney: response }) => {
      const { id: workflowId, projectId } = response?.clone;

      dispatch(
        addPositiveToast(
          <>
            <strong>{workflowName}</strong> created!
          </>,
          {
            actions: [
              handleSwitchToProjectJourney
                ? {
                    title: 'Go to journey',
                    onClick: () => {
                      handleSwitchToProjectJourney(workflowId, projectId);
                    },
                  }
                : {
                    title: 'Open journey',
                    onClick: () =>
                      dispatch(
                        openModal(SWITCH_PROJECTS_MODAL, {
                          projectId,
                          workflowId,
                          workflowType: WorkflowType.DRAFT,
                        }),
                      ),
                  },
            ],
            timeout: null,
          },
        ),
      );
    },
    onError: (error) => {
      let message = ERROR_MESSAGE;
      if (error.message === LOCALES_BETA_CLONING_JOURNEY_ERROR_MESSAGE) {
        message = isCurrentProjInLocaleBeta
          ? errMsgCopyJourneyFromLocalesBetaToNonBeta
          : errMsgCopyJourneyFromNonBetaToLocalesBeta;
      }
      return dispatch(addNegativeToastWithErrorId(message, 'cloneJourneyToProject'));
    },
  });

  const handleCloneJourney = (
    workflow: TrimmedWorkflow,
    projectId: number,
    includeWebhooks: boolean,
    cloneableCampaigns: CloneFormSchemaType = {},
  ) => {
    const cloneParams: {
      id: number;
      type?: WorkflowType;
    } = {
      id: workflow.id,
      type: workflow.workflowType,
    };

    cloneJourneyToProject({
      variables: {
        command: {
          cloneName: workflow.name,
          clonedFromJourneyId: cloneParams,
          destinationProjectId: projectId,
          includeWebhooks,
          includeCampaigns: getCampaignValues(cloneableCampaigns),
        },
      },
    });
  };
  return { handleCloneJourney };
};
